import React, { PropsWithChildren, useState } from 'react';
import { IBaseViewState } from 'src/common/interfaces/states/BaseViewState';
import { onAddItemDataScanned } from 'src/home-page/business-logic/submit-scanned-data';
import {
  getScannerCallback,
  ScannedDataHandler,
  updateScannerCallback,
} from '../../../devices/scanner-operations';
import { store } from '../../redux/configure-store';
import { BaseViewActions } from '../../redux/reducers/base-view-reducer';
import { pauseWithSpinner } from '../with-spinner/with-spinner';

type PageDetails = {
  content?: JSX.Element;
  scannerCallback?: ScannedDataHandler;
  pageStatus?: PageStatus;
};

export type PageStatus = {
  enableCheckoutButton?: boolean;
  enablePluMenu?: boolean;
  enableSidebar?: boolean;
  enableSearch?: boolean;
  isReplaceablePage?: boolean;
  pageId?: string;
};

let pages: PageDetails[] = [];

let contentSet: React.Dispatch<React.SetStateAction<JSX.Element | undefined>>;

export const setHomePage = (
  content: JSX.Element,
  scannerCallback?: ScannedDataHandler,
  pageStatus?: PageStatus,
): void => {
  // Store start page info
  if (pages.length < 2) {
    const startPageDetails = {
      scannerCallback: getScannerCallback(),
      pageStatus: getPageStatus(),
    };
    pages = [];
    pages.push(startPageDetails);
  }

  // This allows page shift without return to former page
  if (shouldReplaceCurrentPage()) {
    pages.pop();
  }

  // Store new page info
  const newPageDetails = {
    content,
    scannerCallback,
    pageStatus,
  };

  pages.push(newPageDetails);
  showPage(newPageDetails);

  pauseWithSpinner();
};

export const restorePage = (pageId?: string): void => {
  // Skip remove paage if already in desired page
  if (pageId) {
    if (getCurrentPageId() === pageId) {
      return;
    }

    const pageIndex = pages.findIndex(page => page.pageStatus?.pageId === pageId);
    if (pageIndex === -1) {
      return;
    } else {
      pages = pages.slice(0, pageIndex + 1);
      const page = pages[pageIndex];
      showPage(page);
    }
  } else if (pages.length > 1) {
    pages.pop();
    const page = pages[pages.length - 1];
    showPage(page);
  }
};

export const restoreSalesHomePage = (): void => {
  pages = [];
  const salesScannerCallback = onAddItemDataScanned;
  const salesPageStatus: PageStatus = {
    enableCheckoutButton: true,
    enablePluMenu: true,
    enableSidebar: true,
    enableSearch: true,
  };

  updateScannerCallback(salesScannerCallback);
  contentSet(undefined);
  setPageView(salesPageStatus);
};

const showPage = (page: PageDetails): void => {
  updateScannerCallback(page.scannerCallback);
  contentSet(page.content);
  setPageView(page.pageStatus);
};

export const EmeraldHomePage: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [content, setContent] = useState<JSX.Element>();

  contentSet = setContent;

  return <>{content || children}</>;
};

// Checkout, PLU menu, sidebar commands, and promostion button are disabled as default
// TODO once all status set here. Move all the status set into one reducer
const setPageView = (status?: PageStatus): void => {
  const baseViewState: IBaseViewState = {
    checkoutButtonDisabled: !status?.enableCheckoutButton,
    pluMenuDisabled: !status?.enablePluMenu,
    searchLockAndMiddleButtonDisabled: !status?.enableSearch,
    sidebarDisabled: !status?.enableSidebar,
  };
  store.dispatch(BaseViewActions.setState(baseViewState));
};

const shouldReplaceCurrentPage = () => {
  return pages.length && pages[pages.length - 1].pageStatus?.isReplaceablePage;
};

const getCurrentPageId = () => {
  return pages.length && pages[pages.length - 1].pageStatus?.pageId;
};

const getPageStatus = (): PageStatus => {
  const baseViewState = store.getState().baseView;
  return {
    enableCheckoutButton: !baseViewState.checkoutButtonDisabled,
    enablePluMenu: !baseViewState.pluMenuDisabled,
    enableSearch: !baseViewState.searchLockAndMiddleButtonDisabled,
    enableSidebar: !baseViewState.sidebarDisabled,
  };
};
